import { ReactComponent as CalendarSolid } from '@assets/icons/calendar-plus.svg';

import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList
} from '@chakra-ui/react';

import { convertTimestampToDate, getCalendarUrl } from 'utils';

import { CalendarType } from 'types/client.types';

import { Button } from 'ui';

const calendarButtons = [
  {
    label: 'Google Calendar',
    value: CalendarType.google
  },
  {
    label: 'Apple Calendar',
    value: CalendarType.apple
  },
  {
    label: 'Yahoo Calendar',
    value: CalendarType.yahoo
  },
  {
    label: 'Outlook Calendar',
    value: CalendarType.outlook
  }
];

interface AddToCalendarProps {
  providerNames?: string;
  appointmentName?: string;
  appointmenStartDate?: number | string;
  appointmenEndDate?: number | string;
  location: string;
  tzOffset: number;
  iconColor?: string;
  isDefault?: boolean;
}

const AddToCalendar = ({
  providerNames,
  appointmentName,
  appointmenStartDate = 0,
  appointmenEndDate = 0,
  location,
  tzOffset,
  iconColor = '',
  isDefault = false
}: AddToCalendarProps) => {
  const handleAddToCalendar = (calendarType: CalendarType) => {
    if (!(appointmenStartDate && appointmenEndDate)) return;

    const eventTitle = appointmentName || 'Appointment';
    const calendarUrl = getCalendarUrl({
      calendarType,
      eventTitle,
      eventStartDate: convertTimestampToDate(appointmenStartDate),
      eventEndDate: convertTimestampToDate(appointmenEndDate),
      eventDetails: `${eventTitle}${
        providerNames ? ' with ' : ''
      }${providerNames}`,
      location,
      tzOffset
    });

    if (calendarType === 'apple') {
      // For Apple Calendar, create a blob and download
      const blob = new Blob([calendarUrl], {
        type: 'text/calendar;charset=utf-8'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${eventTitle.replace(/[^a-z0-9]/gi, '_')}.ics`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      window.open(calendarUrl, '_blank');
    }
  };

  const labelButton = isDefault ? (
    <Button
      variant='outline'
      w='272px'
      onClick={() => handleAddToCalendar(CalendarType.google)}
    >
      Add to Calendar
    </Button>
  ) : (
    <MenuButton as={Button} variant='outline' w='272px'>
      Add to Calendar
    </MenuButton>
  );

  return (
    <Menu>
      {iconColor ? (
        <MenuButton
          as={IconButton}
          variant='ghost'
          bg='transparent'
          aria-label='add to calendar'
          minW='auto'
          width={30}
          height={30}
          icon={<CalendarSolid fill={iconColor} width={30} height={30} />}
          _hover={{
            opacity: 0.7
          }}
          cursor='pointer'
        />
      ) : (
        labelButton
      )}
      <MenuList
        border='none'
        borderRadius='5px'
        p={0}
        minW='130px'
        boxShadow='md'
        zIndex={9999}
      >
        {calendarButtons.map((btn) => (
          <MenuItem
            key={btn.value}
            px={3}
            py={2}
            fontSize='13px'
            _hover={{ bg: 'primary.200' }}
            onClick={() => handleAddToCalendar(btn.value)}
          >
            Add To {btn.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default AddToCalendar;
